import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter, skip } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/// The site's theme as /api/Site/Theme gives it: its key and every token, by the token's name ("primaryDeep").
export interface SiteTheme {
    key: string;
    tokens: { [token: string]: string };
}

const THEME = makeStateKey<SiteTheme>('theme');

/// "primaryDeep" -> "--ph-primary-deep" - the same rule as ThemeTokens.CssVar on the server.
export function themeCssVar(token: string): string {
    return '--ph-' + token.replace(/[A-Z]/g, c => '-' + c.toLowerCase());
}

/// ":root{--ph-primary:#0E3A5D;...}" for a theme's tokens.
export function themeCss(tokens: { [token: string]: string }): string {
    const rules = Object.keys(tokens || {})
        .filter(name => /^#[0-9a-fA-F]{6}$/.test(tokens[name]))
        .map(name => themeCssVar(name) + ':' + tokens[name] + ';');
    return ':root{' + rules.join('') + '}';
}

/// The switchable theme (Theme_Spec.md 2). The stylesheet's colours are CSS custom properties (--ph-*) whose defaults
/// are Harbour's (src/styles/_theme-tokens.scss); this sets them to the site's theme.
///
/// The server render asks /api/Site/Theme, writes the tokens into a <style id="theme"> in the head - so the first paint
/// is already themed, with no flash - and carries the theme in TransferState, so the browser does not ask again. In the
/// browser it is applied with style.setProperty on <html>; on each navigation the page asks again - a 304 against the
/// ETag while nothing changed - and repaints if the owner changed the theme meanwhile. The owner's Appearance page
/// applies a new theme at once.
///
/// A failed call changes nothing: the page keeps the theme it has (or Harbour's defaults).
@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    private current: SiteTheme | null = null;

    constructor(
        private http: HttpClient,
        private state: TransferState,
        router: Router,
        @Inject(DOCUMENT) private document: any,
        @Inject(PLATFORM_ID) private platformId: any,
    ) {
        const carried = state.get<SiteTheme>(THEME, null);
        if (carried && isPlatformBrowser(platformId)) {
            this.apply(carried);
        } else {
            this.load();
        }

        if (isPlatformBrowser(platformId)) {
            // The first NavigationEnd is the page's own load, which already has its theme.
            router.events.pipe(filter(e => e instanceof NavigationEnd), skip(1)).subscribe(() => this.load());
        }
    }

    /// The theme the page shows now, or null before the first answer.
    get theme(): SiteTheme | null {
        return this.current;
    }

    /// Paint the page with a theme.
    apply(theme: SiteTheme): void {
        if (!theme || !theme.tokens) {
            return;
        }
        this.current = theme;

        if (isPlatformServer(this.platformId)) {
            // The head style the first paint uses; after the stylesheet's defaults, so it wins over them.
            let style = this.document.getElementById('theme');
            if (!style) {
                style = this.document.createElement('style');
                style.setAttribute('id', 'theme');
                this.document.head.appendChild(style);
            }
            style.textContent = themeCss(theme.tokens);
        } else {
            const root = this.document.documentElement;
            for (const name of Object.keys(theme.tokens)) {
                if (/^#[0-9a-fA-F]{6}$/.test(theme.tokens[name])) {
                    root.style.setProperty(themeCssVar(name), theme.tokens[name]);
                }
            }
        }

        // The browser's own chrome (the address bar on phones) in the theme's primary.
        const meta = this.document.querySelector('meta[name="theme-color"]');
        if (meta && theme.tokens.primary) {
            meta.setAttribute('content', theme.tokens.primary);
        }
    }

    private load(): void {
        // The browser revalidates with the ETag: a 304, the copy it holds, while the theme is unchanged.
        this.http.get<SiteTheme>(environment.apiUrl + '/api/Site/Theme').subscribe(theme => {
            if (!theme || !theme.tokens) {
                return;
            }
            if (isPlatformServer(this.platformId)) {
                this.state.set(THEME, theme);
            }
            if (!this.current || JSON.stringify(this.current) !== JSON.stringify(theme)) {
                this.apply(theme);
            }
        }, () => { /* keep the theme the page has */ });
    }
}
